// Signature verification of the ID token (aws-jwt-verify). One verifier per
// user pool + client; its JWKS is cached in memory by the verifier and in
// sessionStorage for the tab, so a page load usually verifies without
// downloading the JWKS again.
//
// API Gateway authorizes every API call anyway: this check only decides
// whether the app should treat the session as signed in, so it runs in the
// background (see SessionProvider) and never blocks the first paint.
//
// aws-jwt-verify is loaded lazily, on the first verifyToken() call: importing
// the package (or its index) never loads it. That keeps it out of the apps'
// main bundles and out of every test that doesn't verify a token (jest 27
// can't resolve its "#node-web-compat" subpath import).

import { getSessionConfig, ResolvedSessionConfig } from "./config";
import type { TokenClaims } from "./token";

/** Verification couldn't run (JWKS unreachable): says nothing about the token. */
export class VerificationUnavailableError extends Error {
	readonly unavailable = true;
	constructor(message: string) {
		super(message);
		this.name = "VerificationUnavailableError";
	}
}

/** True for an error that means "couldn't check", not "the token is invalid". */
export const isVerificationUnavailable = (error: unknown): boolean =>
	error instanceof VerificationUnavailableError ||
	Boolean(error && (error as { unavailable?: unknown }).unavailable === true) ||
	// aws-jwt-verify's own FetchError (it refetches the JWKS for an unknown kid)
	Boolean(error && typeof (error as { message?: unknown }).message === "string" && /^Failed to fetch /.test((error as Error).message));

type Verifier = {
	verify: (token: string) => Promise<unknown>;
	cacheJwks: (jwks: any) => void;
};

type VerifierModule = {
	CognitoJwtVerifier: { create: (options: { userPoolId: string; tokenUse: "id"; clientId: string }) => unknown };
};

const verifiers = new Map<string, { verifier: Verifier; hydrated: Promise<void> | null }>();

let verifierModule: Promise<VerifierModule> | null = null;

// The aws-jwt-verify module, loaded once. A failed load (offline, chunk gone
// after a deploy) says nothing about the token: VerificationUnavailableError,
// and the next call tries again.
function loadVerifierModule(): Promise<VerifierModule> {
	if (!verifierModule) {
		const loading = import("aws-jwt-verify").then(
			(module) => module as unknown as VerifierModule,
			(error: unknown) => {
				throw new VerificationUnavailableError(`verifier unavailable: ${(error as Error)?.message ?? error}`);
			}
		);
		verifierModule = loading;
		loading.catch(() => {
			if (verifierModule === loading) verifierModule = null;
		});
	}
	return verifierModule;
}

const jwksUrl = (config: ResolvedSessionConfig) =>
	`https://cognito-idp.${config.region}.amazonaws.com/${config.userPoolId}/.well-known/jwks.json`;

const storageKey = (config: ResolvedSessionConfig) => `web-session:jwks:${config.userPoolId}`;

const readStoredJwks = (config: ResolvedSessionConfig): unknown | null => {
	try {
		const raw = window.sessionStorage.getItem(storageKey(config));
		return raw ? JSON.parse(raw) : null;
	} catch {
		return null;
	}
};

const storeJwks = (config: ResolvedSessionConfig, jwks: unknown) => {
	try {
		window.sessionStorage.setItem(storageKey(config), JSON.stringify(jwks));
	} catch {
		// storage full/unavailable: the in-memory copy still serves this page
	}
};

const forgetStoredJwks = (config: ResolvedSessionConfig) => {
	try {
		window.sessionStorage.removeItem(storageKey(config));
	} catch {
		// nothing stored
	}
};

async function fetchJwks(config: ResolvedSessionConfig): Promise<unknown> {
	let response: Response;
	try {
		response = await fetch(jwksUrl(config));
	} catch (error) {
		throw new VerificationUnavailableError(`JWKS unreachable: ${(error as Error)?.message ?? error}`);
	}
	if (!response.ok) throw new VerificationUnavailableError(`JWKS request failed: ${response.status}`);
	try {
		return await response.json();
	} catch {
		throw new VerificationUnavailableError("JWKS response is not JSON");
	}
}

// Loads the JWKS into the verifier once: from sessionStorage when this tab
// already has it, else from Cognito (then stored).
function hydrate(config: ResolvedSessionConfig, verifier: Verifier): Promise<void> {
	const stored = readStoredJwks(config);
	if (stored) {
		try {
			verifier.cacheJwks(stored);
			return Promise.resolve();
		} catch {
			forgetStoredJwks(config); // corrupted: fetch it again
		}
	}
	return fetchJwks(config).then((jwks) => {
		try {
			verifier.cacheJwks(jwks);
		} catch (error) {
			throw new VerificationUnavailableError(`JWKS invalid: ${(error as Error)?.message ?? error}`);
		}
		storeJwks(config, jwks);
	});
}

async function verifierFor(config: ResolvedSessionConfig) {
	const key = `${config.userPoolId}|${config.clientId}`;
	let entry = verifiers.get(key);
	if (!entry) {
		const { CognitoJwtVerifier } = await loadVerifierModule();
		// another call may have created it while the module loaded
		const existing = verifiers.get(key);
		if (existing) return existing;
		const verifier = CognitoJwtVerifier.create({
			userPoolId: config.userPoolId,
			tokenUse: "id",
			clientId: config.clientId,
		}) as unknown as Verifier;
		entry = { verifier, hydrated: null };
		verifiers.set(key, entry);
	}
	return entry;
}

/**
 * Verifies the ID token's signature, issuer, audience, token_use and expiry.
 * Resolves with its claims; rejects when it is invalid -- or with a
 * VerificationUnavailableError when the JWKS couldn't be loaded.
 */
export async function verifyToken(token: string, config: ResolvedSessionConfig = getSessionConfig()): Promise<TokenClaims> {
	const entry = await verifierFor(config);
	if (!entry.hydrated) {
		const loading = hydrate(config, entry.verifier);
		entry.hydrated = loading;
		// a failed load is retried by the next call
		loading.catch(() => {
			if (entry.hydrated === loading) entry.hydrated = null;
		});
	}
	await entry.hydrated;
	return (await entry.verifier.verify(token)) as TokenClaims;
}

/** tests only */
export function resetVerifiers(): void {
	verifiers.clear();
	verifierModule = null;
}
